import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, ArrowRight, LogIn } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../mock/mockData";
import { useSiteContent } from "../context/SiteContentProvider";
import { SOCIAL_LINKS, MEMBER_LOGIN_URL } from "../config/links";

const Footer = () => {
  const [email, setEmail] = useState("");
  const { memberships } = useSiteContent();
  return (
    <footer className="bg-ink text-white/90">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-20 pb-10">
        {/* Newsletter row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-14 border-b border-white/12">
          <div>
            <h3 className="font-serif-display text-3xl md:text-5xl text-white leading-tight whitespace-nowrap">
              Find a workspace you'll love.
            </h3>
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); setEmail(""); }}
            className="flex items-center gap-3 w-full lg:w-auto"
          >
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Your email"
              className="flex-1 lg:w-72 bg-transparent border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-camel transition-colors"
            />
            <button className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-camel text-white label-caps text-[11px] btn-press hover:bg-camel-dark">
              Subscribe <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Links */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 py-14">
          <div className="col-span-2 md:col-span-5">
            <Logo light />
            <div className="flex gap-3 mt-7">
              {[
                { Icon: Instagram, href: SOCIAL_LINKS.instagram },
                { Icon: Linkedin, href: SOCIAL_LINKS.linkedin },
                { Icon: Facebook, href: SOCIAL_LINKS.facebook },
              ].map(({ Icon, href }) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer" aria-label="social" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-camel hover:border-camel transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <a
              href={MEMBER_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-member-login-link"
              className="mt-8 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full border border-camel text-camel label-caps text-[12px] hover:bg-camel hover:text-white transition-colors"
            >
              <LogIn className="w-[18px] h-[18px]" /> Member Login
            </a>
          </div>
          <div className="md:col-span-3">
            <h4 className="label-caps text-[11px] text-camel mb-5">Workspaces</h4>
            <ul className="space-y-3">
              {memberships.map((m) => (
                <li key={m.slug}>
                  <Link to={`/memberships/${m.slug}`} className="text-sm text-white/60 hover:text-white transition-colors">{m.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <h4 className="label-caps text-[11px] text-camel mb-5">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm text-white/60 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/amenities" className="text-sm text-white/60 hover:text-white transition-colors">Amenities</Link></li>
              <li><Link to="/blog" className="text-sm text-white/60 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-sm text-white/60 hover:text-white transition-colors">Contact</Link></li>
              <li><a href={`mailto:${COMPANY.email}`} className="text-sm text-white/60 hover:text-white transition-colors">{COMPANY.email}</a></li>
              <li><a href={`tel:${COMPANY.phone}`} className="text-sm text-white/60 hover:text-white transition-colors">{COMPANY.phone}</a></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pt-8 border-t border-white/12">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} Bond Collective. All rights reserved.</p>
          <p className="text-xs text-white/40">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span className="mx-2">·</span>
            <Link to="/resources" data-testid="footer-resources-link" className="hover:text-white transition-colors">Member Terms</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
